import { Button } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { usePlotStore } from '../../../../stores/plotStore'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import type { CharacterInput, CharacterResult, SavedCharacter, DetailedLockedSections } from '@creanizer/types'
import { AlertCircle, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { CharacterForm } from './CharacterForm'
import { CharacterResult as CharacterResultComponent } from './CharacterResult'
import { SavedCharactersList } from './SavedCharactersList'

export function CharacterGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const generator = useGenerator('character')
  const streamGenerator = useStreamGenerator('character')
  const savedPlots = usePlotStore((state) => state.savedPlots)
  const { characters, addCharacter } = useCharacterStore()
  
  const [input, setInput] = useState<CharacterInput>({
    mode: 'standard',
    creativityLevel: 7
  })
  const [result, setResult] = useState<CharacterResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSaved, setIsSaved] = useState(false)
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [activeTab, setActiveTab] = useState('generate')
  const [partialRegenerateMode, setPartialRegenerateMode] = useState(false)
  const [updateMode, setUpdateMode] = useState(false)
  const [existingCharacterData, setExistingCharacterData] = useState<CharacterResult | null>(null)
  const [lockedSections, setLockedSections] = useState<DetailedLockedSections>({})
  const [updateSource, setUpdateSource] = useState<string>('')

  const handleGenerate = async (preparedInput?: CharacterInput) => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定画面でGemini APIキーを入力してください。')
      return
    }

    setError(null)
    setResult(null)
    setIsSaved(false)

    try {
      let finalInput = preparedInput || input
      
      // 部分再生成モードの場合
      if (partialRegenerateMode && existingCharacterData) {
        finalInput = {
          ...finalInput,
          mode: 'partial_regenerate' as const,
          existingResult: existingCharacterData,
          lockedSections: lockedSections
        }
      }

      // 情報更新モードの場合
      if (updateMode && existingCharacterData) {
        finalInput = {
          ...finalInput,
          mode: 'update' as const,
          existingResult: existingCharacterData,
          updateSource: updateSource
        }
      }
      
      if (useStreaming) {
        await streamGenerator.generateStream(finalInput, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              // 部分再生成モードの場合
              if (partialRegenerateMode && existingCharacterData) {
                // 部分再生成の結果は既にマージ済みのはずなので、そのまま使用
                console.log('Partial regenerate result:', resultData)
                setResult(resultData as CharacterResult)
              } 
              // 通常モードの場合
              else if (typeof resultData === 'object' && ('name' in resultData || 'basicInfo' in resultData)) {
                setResult(resultData as CharacterResult)
              } else {
                console.error('Invalid result structure:', resultData)
                // 構造が不正でも、データがあればとりあえずセット
                if (resultData) {
                  console.warn('Setting result despite structure issues')
                  setResult(resultData as CharacterResult)
                } else {
                  setError('結果のパースに失敗しました')
                }
              }
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            // ストリーミングエラーの場合は通常モードにフォールバック
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(finalInput)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(finalInput)
        if (response?.result) {
          setResult(response.result as CharacterResult)
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'キャラクター生成中にエラーが発生しました')
    }
  }

  const handleSave = () => {
    if (!result) return

    // _changeInfoを除外した結果を保存（一時的な変更情報は保存しない）
    const { _changeInfo, ...resultWithoutChangeInfo } = result

    const newCharacter = {
      id: crypto.randomUUID(),
      title: `${result.name} - ${result.role}`,
      input,
      result: resultWithoutChangeInfo,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    addCharacter(newCharacter)
    setIsSaved(true)
    // 保存後は変更情報をクリア
    setResult(resultWithoutChangeInfo)
  }

  const handleLoadSaved = (character: SavedCharacter) => {
    setInput(character.input)
    setResult(character.result)
    setIsSaved(true)
    setExistingCharacterData(character.result)
    setPartialRegenerateMode(true)
    setUpdateMode(false)
    setUpdateSource('')
    setLockedSections({}) // 全項目をアンロック状態で開始
    setActiveTab('generate')
  }

  const handleLoadForUpdate = (character: SavedCharacter) => {
    setInput(character.input)
    setResult(null)
    setIsSaved(false)
    setExistingCharacterData(character.result)
    setPartialRegenerateMode(false)
    setUpdateMode(true)
    setUpdateSource('')
    setActiveTab('generate')
  }


  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5" />
          <h2 className="text-xl font-semibold">キャラクター設定ジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          生きたキャラクターを感じられる詳細な設定を生成
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({characters.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            <CharacterForm
              input={input}
              onInputChange={setInput}
              onGenerate={handleGenerate}
              isGenerating={generator.isPending || streamGenerator.isStreaming}
              savedPlots={savedPlots}
              useStreaming={useStreaming}
              onStreamingChange={setUseStreaming}
              partialRegenerateMode={partialRegenerateMode}
              existingResult={existingCharacterData}
              lockedSections={lockedSections}
              onLockedSectionsChange={setLockedSections}
              onPartialRegenerateModeChange={setPartialRegenerateMode}
              updateMode={updateMode}
              updateSource={updateSource}
              onUpdateSourceChange={setUpdateSource}
              onUpdateModeChange={setUpdateMode}
            />
          </div>

          {streamGenerator.isStreaming && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <CharacterResultComponent
              result={result}
              onSave={handleSave}
              isSaved={isSaved}
              onEdit={(updatedResult) => {
                setResult({ ...result, ...updatedResult })
                setIsSaved(false)
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="saved">
          <SavedCharactersList
            characters={characters}
            onLoad={handleLoadSaved}
            onUpdate={handleLoadForUpdate}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}